import { useState, type FormEvent, type ReactNode } from 'react';
import { useT } from '@/shared/i18n';
import {
  Button,
  ErrorState,
  FormField,
  Input,
  Select,
  Textarea,
} from '@/shared/ui/primitives';
import { ApiError } from '@/shared/api/api-error';
import { submitEarlyAccessRequest } from '../services/early-access.service';
import type {
  EarlyAccessInterestedAs,
  EarlyAccessRequestInput,
} from '../models/early-access.types';

interface FormValues {
  name: string;
  email: string;
  interestedAs: EarlyAccessInterestedAs | '';
  company: string;
  role: string;
  useCase: string;
  consent: boolean;
}

type FieldErrors = Partial<Record<keyof FormValues, string>>;

const initialValues: FormValues = {
  name: '',
  email: '',
  interestedAs: 'candidate',
  company: '',
  role: '',
  useCase: '',
  consent: false,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Query params let the Google OAuth "no account" redirect prefill the form. */
function readQuery(): URLSearchParams {
  return new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.search,
  );
}

function prefilledValues(): FormValues {
  const query = readQuery();
  return {
    ...initialValues,
    name: query.get('name') ?? initialValues.name,
    email: query.get('email') ?? initialValues.email,
  };
}

export function EarlyAccessForm(): ReactNode {
  const { locale, t } = useT();
  const fromGoogle = readQuery().get('from') === 'google';
  const [values, setValues] = useState<FormValues>(prefilledValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  function update<K extends keyof FormValues>(key: K, value: FormValues[K]): void {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setApiError(null);
  }

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (values.name.trim().length === 0) {
      next.name = t('earlyAccess.validation.name');
    }
    if (!emailPattern.test(values.email.trim())) {
      next.email = t('earlyAccess.validation.email');
    }
    if (!values.interestedAs) {
      next.interestedAs = t('earlyAccess.validation.interestedAs');
    }
    if (!values.consent) {
      next.consent = t('earlyAccess.validation.consent');
    }
    return next;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !values.interestedAs) return;

    setIsSubmitting(true);
    setApiError(null);
    try {
      const payload: EarlyAccessRequestInput = {
        name: values.name.trim(),
        email: values.email.trim(),
        interestedAs: values.interestedAs,
        locale,
        source: 'landing-final-cta',
      };
      const company = optional(values.company);
      const role = optional(values.role);
      const useCase = optional(values.useCase);
      if (company) payload.company = company;
      if (role) payload.role = role;
      if (useCase) payload.useCase = useCase;

      await submitEarlyAccessRequest(payload);
      setIsSuccess(true);
      setValues(initialValues);
    } catch (error) {
      setApiError(error instanceof ApiError ? error.message : t('earlyAccess.error.body'));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSuccess) {
    return (
      <section
        className="mx-auto max-w-2xl rounded-lg border border-[var(--ink-band-border)] bg-[var(--ink-band-raised)] p-6 text-start shadow-xl shadow-black/10"
        aria-live="polite"
      >
        <p className="text-sm font-semibold uppercase tracking-wider text-[var(--on-ink-band-accent)]">
          {t('earlyAccess.success.title')}
        </p>
        <p className="mt-3 text-base leading-7 text-[var(--on-ink-band)]">
          {t('earlyAccess.success.body')}
        </p>
      </section>
    );
  }

  return (
    <form
      className="mx-auto max-w-3xl rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-5 text-start shadow-xl shadow-black/10 md:p-6"
      onSubmit={(event) => void onSubmit(event)}
      noValidate
    >
      <div className="mb-5 text-center md:text-start">
        <h2 className="text-3xl font-bold tracking-tight text-[var(--text-primary)] md:text-4xl">
          {t('earlyAccess.title')}
        </h2>
        <p className="mt-3 text-base leading-7 text-[var(--text-secondary)]">
          {t('earlyAccess.subtitle')}
        </p>
      </div>

      {fromGoogle ? (
        <div
          className="mb-5 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-container)] p-4 text-sm text-[var(--text-secondary)]"
          role="status"
        >
          {t('earlyAccess.fromGoogle.notice')}
        </div>
      ) : null}

      {apiError ? (
        <ErrorState
          className="mb-5"
          title={t('earlyAccess.error.title')}
          messages={[apiError]}
        />
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label={t('earlyAccess.field.name')} error={errors.name}>
          <Input
            value={values.name}
            invalid={Boolean(errors.name)}
            maxLength={120}
            placeholder={t('earlyAccess.placeholder.name')}
            onChange={(event) => update('name', event.currentTarget.value)}
          />
        </FormField>
        <FormField label={t('earlyAccess.field.email')} error={errors.email}>
          <Input
            type="email"
            value={values.email}
            invalid={Boolean(errors.email)}
            maxLength={254}
            placeholder={t('earlyAccess.placeholder.email')}
            onChange={(event) => update('email', event.currentTarget.value)}
          />
        </FormField>
        <FormField
          label={t('earlyAccess.field.interestedAs')}
          error={errors.interestedAs}
        >
          <Select
            value={values.interestedAs}
            invalid={Boolean(errors.interestedAs)}
            onChange={(event) =>
              update(
                'interestedAs',
                event.currentTarget.value as EarlyAccessInterestedAs,
              )
            }
          >
            <option value="candidate">{t('earlyAccess.option.candidate')}</option>
            <option value="employer">{t('earlyAccess.option.employer')}</option>
          </Select>
        </FormField>
        <FormField label={t('earlyAccess.field.company')}>
          <Input
            value={values.company}
            maxLength={160}
            placeholder={t('earlyAccess.placeholder.company')}
            onChange={(event) => update('company', event.currentTarget.value)}
          />
        </FormField>
        <FormField label={t('earlyAccess.field.role')}>
          <Input
            value={values.role}
            maxLength={160}
            placeholder={t('earlyAccess.placeholder.role')}
            onChange={(event) => update('role', event.currentTarget.value)}
          />
        </FormField>
        <FormField label={t('earlyAccess.field.useCase')} className="md:col-span-2">
          <Textarea
            value={values.useCase}
            maxLength={500}
            rows={3}
            placeholder={t('earlyAccess.placeholder.useCase')}
            onChange={(event) => update('useCase', event.currentTarget.value)}
          />
        </FormField>
      </div>

      <label className="mt-5 flex items-start gap-3 text-sm text-[var(--text-secondary)]">
        <input
          type="checkbox"
          checked={values.consent}
          aria-invalid={Boolean(errors.consent) || undefined}
          className="mt-1 h-4 w-4 rounded border-[var(--border-strong)] accent-[var(--button-primary-bg)]"
          onChange={(event) => update('consent', event.currentTarget.checked)}
        />
        <span>
          {t('earlyAccess.field.consent')}
          {errors.consent ? (
            <span className="mt-1 block font-medium text-[var(--danger-text)]">
              {errors.consent}
            </span>
          ) : null}
        </span>
      </label>

      <div className="mt-6 flex justify-center md:justify-start">
        <Button type="submit" size="lg" disabled={isSubmitting}>
          {isSubmitting ? t('earlyAccess.submitting') : t('earlyAccess.submit')}
        </Button>
      </div>
    </form>
  );
}
